import { Link } from "react-router-dom";
import { ShieldCheck, Target, Bot, TrendingUp, Heart, Lock, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const STEPS = [
  { icon: Target, title: "1 · Pick your goal", text: "Get a job, start a business, launch a side hustle, go freelance or grow your customers." },
  { icon: Bot, title: "2 · AI builds your plan", text: "In ~60 seconds you get a personalised day-by-day roadmap, documents and assets — automatically." },
  { icon: TrendingUp, title: "3 · Take action daily", text: "Your dashboard shows the single Next Best Action. Complete tasks, earn points, build your Success Score." },
  { icon: Sparkles, title: "4 · Your AI coach keeps you going", text: "Weekly check-ins review your progress, celebrate wins and update your roadmap. Real outcomes get tracked." },
];

// Single config point for founder social links — paste official URLs here.
// Links with empty URLs are hidden automatically. DO NOT guess URLs.
const FOUNDER_SOCIALS = {
  linkedin: "https://www.linkedin.com/in/olamide-ojo-040ab153",
  facebook: "https://www.facebook.com/share/19ZepPJbZx/?mibextid=wwXIfr",
  instagram: "https://www.instagram.com/lahmide",
  threads: "https://www.threads.com/@lahmide",
  tiktok: "https://www.tiktok.com/@zuntrai.ai",
  youtube: "https://youtube.com/@zuntria",
  website: "https://zuntria.co.uk",
};

export default function AboutPage() {
  return (
    <div className="mx-auto max-w-5xl px-6 py-12 md:px-10" data-testid="about-page">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">About Zuntria</p>
      <h1 className="mt-3 max-w-3xl font-display text-4xl font-medium tracking-tighter sm:text-5xl">
        We turn goals into <span className="text-accent">income</span> — automatically.
      </h1>
      <p className="mt-5 max-w-2xl text-muted-foreground">
        Zuntria is the UK's autonomous success system. Most AI tools answer questions — Zuntria gets things done:
        it plans your path, generates your documents, tracks your real-world results, and coaches you week after week
        until you hit your goal. No consultants. No waiting. No manual work.
      </p>

      {/* How it works */}
      <section className="mt-14">
        <h2 className="font-display text-lg">How Zuntria works</h2>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {STEPS.map((s) => (
            <div key={s.title} className="rounded-2xl border border-border bg-card p-6">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <s.icon className="h-4 w-4" />
              </span>
              <h3 className="mt-3 font-display text-base">{s.title}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Founder story */}
      <section className="mt-14 rounded-2xl border border-border bg-card p-6 md:p-10">
        <div className="flex items-center gap-2">
          <Heart className="h-4 w-4 text-accent" />
          <h2 className="font-display text-lg">Why we built this</h2>
        </div>
        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-muted-foreground">
          Zuntria was founded in London by Olamide Emmanuel Ojo, who saw the same story everywhere: talented,
          hard-working people stuck — not because they lacked ability, but because they lacked a clear plan and someone
          in their corner. Career advice was expensive. Business consultants were out of reach. So we built a system
          that gives everyone what the privileged few always had: a personal strategist, a document team and an
          accountability coach — powered by AI, available 24/7, starting free.
        </p>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
          Today Zuntria helps members across the UK get hired, launch businesses, win clients and build savings
          together through Ajo groups — and every win logged by our community makes the system smarter.
        </p>
      </section>

      {/* Trust */}
      <section className="mt-14">
        <h2 className="font-display text-lg">Your data, protected</h2>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-border bg-card p-5">
            <ShieldCheck className="h-4 w-4 text-emerald-500" />
            <h3 className="mt-2 text-sm font-medium">Made in the UK 🇬🇧</h3>
            <p className="mt-1 text-xs text-muted-foreground">Built and operated in London, designed around UK rules — HMRC, sole trader setup, UK job market.</p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5">
            <Lock className="h-4 w-4 text-primary" />
            <h3 className="mt-2 text-sm font-medium">Encrypted & private</h3>
            <p className="mt-1 text-xs text-muted-foreground">256-bit TLS everywhere. Your documents and chats are yours — view and clear what the AI remembers any time in Settings.</p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5">
            <ShieldCheck className="h-4 w-4 text-accent" />
            <h3 className="mt-2 text-sm font-medium">Honest AI</h3>
            <p className="mt-1 text-xs text-muted-foreground">AI can make mistakes. Always double-check legal, tax and financial outputs — our plans are guidance, not regulated advice.</p>
          </div>
        </div>
      </section>

      {/* Founders */}
      <section className="mt-14" data-testid="founders-section">
        <h2 className="font-display text-lg">The founder</h2>
        <div className="mt-5 grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="overflow-hidden rounded-2xl border border-border bg-card">
            <img
              src="https://customer-assets.emergentagent.com/job_zuntria-chat/artifacts/gkd8absm_797A0DBA-4926-4235-8336-16D5281766F8.png"
              alt="Olamide — Founder, Zuntria"
              className="aspect-[4/5] w-full object-cover"
              data-testid="founder-portrait-1"
            />
            <div className="p-5">
              <h3 className="font-display text-lg">Olamide</h3>
              <p className="text-xs uppercase tracking-wider text-primary">Founder &amp; CEO</p>
              <p className="mt-3 text-sm text-muted-foreground">
                Built Zuntria to turn every ambition into a plan you can actually finish. Believes AI should
                do the work — not just answer questions.
              </p>
              <div className="mt-4 flex flex-wrap gap-2" data-testid="founder-social-links">
                {Object.entries(FOUNDER_SOCIALS).filter(([, url]) => url).map(([name, url]) => (
                  <a key={name} href={url} target="_blank" rel="noreferrer" aria-label={`Founder on ${name}`}
                    className="rounded-full border border-border bg-secondary px-3 py-1 text-xs capitalize text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                    data-testid={`founder-${name}-link`}>
                    {name}
                  </a>
                ))}
              </div>
            </div>
          </div>
          <div className="overflow-hidden rounded-2xl border border-border bg-card">
            <img
              src="https://customer-assets.emergentagent.com/job_zuntria-chat/artifacts/vxnkympq_A36765E4-C2CB-4D52-85D7-A40258FDA61C.png"
              alt="Olamide — Founder, Zuntria"
              className="aspect-[4/5] w-full object-cover"
              data-testid="founder-portrait-2"
            />
            <div className="p-5">
              <h3 className="font-display text-lg">Building in the open</h3>
              <p className="text-xs uppercase tracking-wider text-accent">Made in the UK</p>
              <p className="mt-3 text-sm text-muted-foreground">
                Zuntria is founder-led and independent. Every feature is shipped in public and refined
                based on real user wins.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Founder's book */}
      <section className="mt-14 rounded-2xl border border-border bg-card p-6 md:p-10" data-testid="founder-book-section">
        <div className="grid gap-8 md:grid-cols-[240px_1fr] md:items-center">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/57ee598e-7cea-410b-a94f-0984571bd71d/images/7296781ac8cdf4705c3f647ca749a9dedacdb1fe2d005865a8ceeca87b17ea23.jpeg"
            alt="What No One Taught You — book by Olamide Ojo"
            className="mx-auto w-48 rounded-xl border border-border shadow-2xl md:w-full"
            data-testid="founder-book-cover"
          />
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-accent">The Observer's Notebook · Vol. 1</p>
            <h2 className="mt-2 font-display text-2xl">What No One Taught You</h2>
            <p className="text-sm text-muted-foreground">The Unspoken Rules of Human Behavior — by Olamide Ojo</p>
            <p className="mt-4 text-sm text-muted-foreground">
              69 pages of sharp, practical observations on the hidden rules that shape every room you walk into —
              fear, focus, memory, influence and the unspoken dynamics no one teaches you. Written by the founder,
              in the same spirit Zuntria is built: see the game clearly, then execute.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Button asChild data-testid="founder-book-buy-btn">
                <Link to="/marketplace?q=What%20No%20One%20Taught%20You">Get the eBook — £7.99</Link>
              </Button>
              <span className="text-xs text-muted-foreground">Instant PDF download after purchase</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mt-14 rounded-2xl border border-accent/40 bg-gradient-to-br from-accent/10 via-card to-card p-8 text-center">
        <h2 className="font-display text-2xl">Ready to see your roadmap?</h2>
        <p className="mt-2 text-sm text-muted-foreground">Free, personalised, ready in 60 seconds — no account needed.</p>
        <Button asChild size="lg" className="mt-5 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="about-cta">
          <Link to="/free-roadmap">Get my free roadmap <ArrowRight className="ml-2 h-4 w-4" /></Link>
        </Button>
      </section>
    </div>
  );
}
